import React, { useState, useEffect, useCallback, useRef } from 'react';
import { shippingApi } from '../../api/shippingApi';
import { formatDateYmdInJst } from '../../utils/formatDateTime';

const HISTORY_LOAD_DAYS = 31;
const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

function formatDateLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const dow = WEEKDAY_LABELS[d.getDay()];
  return `${month}/${day}(${dow})`;
}

function subtractDays(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() - days);
  return formatDateYmdInJst(d);
}

function applyHistoryData(data, { mergePast = false, prevDates = [], prevRows = [] } = {}) {
  const productDisps = data.rows.map((row) => row.product_disp);
  const dates = mergePast ? [...data.dates, ...prevDates] : data.dates;
  const rows = productDisps.map((productDisp, idx) => {
    const apiRow = data.rows[idx];
    const historyQuantities = mergePast
      ? [...apiRow.history_quantities, ...(prevRows[idx]?.historyQuantities || [])]
      : apiRow.history_quantities;
    return {
      productDisp,
      historyQuantities,
    };
  });

  const todayValues = {};
  data.rows.forEach((row) => {
    todayValues[row.product_disp] = row.today_qty ?? null;
  });

  return {
    productDisps,
    dates,
    rows,
    todayValues,
    todayDate: data.today_date,
    lastInputDate: data.last_input_date,
    todayInputTime: data.today_input_time,
    hasMorePast: data.has_more_past,
    historyFromDate: mergePast ? data.history_from_date : data.history_from_date,
    historyToDate: data.history_to_date,
  };
}

/**
 * 仕入先在庫入力共通コンポーネント（現状は宝機材専用）
 */
export const SupplierStockEditor = ({ supplierCode, title = '在庫入力', readOnly = false }) => {
  const [productMasterMap, setProductMasterMap] = useState({});
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingMorePast, setLoadingMorePast] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const [productDisps, setProductDisps] = useState([]);
  const [dates, setDates] = useState([]);
  const [rows, setRows] = useState([]);
  const [todayValues, setTodayValues] = useState({});
  const [todayDate, setTodayDate] = useState(null);
  const [lastInputDate, setLastInputDate] = useState(null);
  const [todayInputTime, setTodayInputTime] = useState(null);
  const [hasMorePast, setHasMorePast] = useState(false);
  const [historyFromDate, setHistoryFromDate] = useState(null);

  const scrollContainerRef = useRef(null);

  const scrollToLatest = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollLeft = el.scrollWidth;
    }
  }, []);

  const loadHistory = useCallback(
    async (fromDate, toDate, { mergePast = false, prevDates = [], prevRows = [] } = {}) => {
      if (!supplierCode) return;
      const res = await shippingApi.getSupplierStockHistory(supplierCode, fromDate, toDate);
      const data = res.data || res;
      const applied = applyHistoryData(data, { mergePast, prevDates, prevRows });

      setProductDisps(applied.productDisps);
      setDates(applied.dates);
      setRows(applied.rows);
      setTodayValues(applied.todayValues);
      setTodayDate(applied.todayDate);
      setLastInputDate(applied.lastInputDate);
      setTodayInputTime(applied.todayInputTime);
      setHasMorePast(applied.hasMorePast);
      setHistoryFromDate(applied.historyFromDate);

      return applied;
    },
    [supplierCode]
  );

  useEffect(() => {
    const init = async () => {
      setLoadingHistory(true);
      try {
        await loadHistory(null, null);
      } catch (error) {
        console.error('仕入先在庫履歴取得エラー:', error);
      } finally {
        setLoadingHistory(false);
      }
    };
    init();
  }, [supplierCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loadingHistory && dates.length >= 0) {
      requestAnimationFrame(scrollToLatest);
    }
  }, [loadingHistory, dates, todayDate, scrollToLatest]);

  useEffect(() => {
    const loadMaster = async () => {
      if (productDisps.length === 0) return;
      const missingDisps = productDisps.filter((code) => !productMasterMap[code]);
      if (missingDisps.length === 0) return;

      setLoadingMaster(true);
      try {
        const res = await shippingApi.getProductsByDisp(missingDisps);
        const list = res.data || res;
        setProductMasterMap((prev) => {
          const map = { ...prev };
          list.forEach((item) => {
            if (!item.product_disp) return;
            const existing = map[item.product_disp] || {};
            map[item.product_disp] = {
              product_name: existing.product_name || item.product_name || '-',
              product_size: item.product_size || existing.product_size || '-',
              tani: item.tani || existing.tani || '-',
            };
          });
          return map;
        });
      } catch (error) {
        console.error('仕入先在庫用製品マスタ取得エラー:', error);
      } finally {
        setLoadingMaster(false);
      }
    };
    loadMaster();
  }, [productDisps, productMasterMap]);

  const handleTodayChange = useCallback((productDisp, value) => {
    if (value === '') {
      setTodayValues((prev) => ({ ...prev, [productDisp]: null }));
      return;
    }
    const num = Number(value);
    setTodayValues((prev) => ({
      ...prev,
      [productDisp]: Number.isNaN(num) ? null : num,
    }));
  }, []);

  const handleLoadMorePast = useCallback(async () => {
    if (!historyFromDate || loadingMorePast) return;
    setLoadingMorePast(true);
    try {
      const newTo = subtractDays(historyFromDate, 1);
      const newFrom = subtractDays(historyFromDate, HISTORY_LOAD_DAYS);
      await loadHistory(newFrom, newTo, { mergePast: true, prevDates: dates, prevRows: rows });
    } catch (error) {
      console.error('過去履歴読み込みエラー:', error);
      alert('過去の履歴の読み込みに失敗しました');
    } finally {
      setLoadingMorePast(false);
    }
  }, [historyFromDate, loadingMorePast, loadHistory, dates, rows]);

  const handleCopyLastToToday = useCallback(async () => {
    if (!supplierCode || !todayDate) return;
    if (!lastInputDate) {
      alert('前回入力日がありません');
      return;
    }

    const hasTodayValues = Object.values(todayValues).some((v) => v !== null);
    if (hasTodayValues) {
      const ok = window.confirm('今日の在庫を前回入力分で上書きします。よろしいですか？');
      if (!ok) return;
    }

    setIsCopying(true);
    try {
      await shippingApi.copySupplierStockLastToToday(supplierCode, todayDate);
      await loadHistory(historyFromDate, todayDate);
    } catch (error) {
      console.error('前回入力分コピーエラー:', error);
      const detail = error.response?.data?.detail;
      alert(detail || '前回入力分のコピーに失敗しました');
    } finally {
      setIsCopying(false);
    }
  }, [supplierCode, todayDate, lastInputDate, todayValues, loadHistory, historyFromDate]);

  const handleSaveToday = useCallback(async () => {
    if (!supplierCode) return;

    const missing = productDisps.filter((p) => todayValues[p] === null || todayValues[p] === undefined);
    if (missing.length > 0) {
      alert(`未入力の品目があります（${productDisps.length}件中${productDisps.length - missing.length}件）`);
      return;
    }

    setIsSaving(true);
    try {
      const items = productDisps.map((product_disp) => ({
        product_disp,
        stock_qty: todayValues[product_disp],
      }));
      await shippingApi.saveSupplierStockToday(supplierCode, items);
      alert('在庫数量を保存しました');
      await loadHistory(historyFromDate, todayDate);
    } catch (error) {
      console.error('在庫数量保存エラー:', error);
      const detail = error.response?.data?.detail;
      alert(detail || '在庫数量の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  }, [supplierCode, productDisps, todayValues, loadHistory, historyFromDate, todayDate]);

  const todayLabel = todayDate ? formatDateLabel(todayDate) : '';
  const lastInputLabel = lastInputDate ? formatDateLabel(lastInputDate) : 'なし';

  return (
    <div className="mb-2 bg-white rounded-lg shadow-sm border border-slate-200 h-full flex flex-col">
      <div className="px-4 pt-4 pb-2 border-b border-slate-200 flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">{title}</p>
          <p className="text-sm text-slate-700">
            今日: <span className="font-semibold">{todayLabel}</span>
          </p>
          <p className="text-sm text-slate-600">
            前回入力日: <span className="font-medium">{lastInputLabel}</span>
          </p>
          {loadingMaster && (
            <p className="text-xs text-slate-400 mt-1">製品情報を読み込み中...</p>
          )}
          {loadingHistory && (
            <p className="text-xs text-slate-400 mt-1">在庫履歴を読み込み中...</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">入力時間</p>
          <p className="text-sm font-semibold text-slate-900">{todayInputTime || '-'}</p>
        </div>
      </div>

      {!readOnly && (
        <div className="px-4 pt-2 pb-2 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {hasMorePast && (
              <button
                type="button"
                onClick={handleLoadMorePast}
                disabled={loadingMorePast}
                className="px-3 py-1.5 text-xs rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                {loadingMorePast ? '読み込み中...' : 'さらに過去を読み込む'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveToday}
              disabled={isSaving || loadingHistory}
              className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? '保存中...' : '保存'}
            </button>
            <button
              type="button"
              onClick={handleCopyLastToToday}
              disabled={isCopying || loadingHistory || !lastInputDate}
              className="px-3 py-1.5 text-xs rounded-md bg-slate-700 text-white hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCopying ? 'コピー中...' : '前回入力分を今日にコピー'}
            </button>
          </div>
        </div>
      )}

      {readOnly && hasMorePast && (
        <div className="px-4 pt-2 pb-2 border-b border-slate-200">
          <button
            type="button"
            onClick={handleLoadMorePast}
            disabled={loadingMorePast}
            className="px-3 py-1.5 text-xs rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-50"
          >
            {loadingMorePast ? '読み込み中...' : 'さらに過去を読み込む'}
          </button>
        </div>
      )}

      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <table className="divide-y divide-slate-200" style={{ minWidth: 'max-content', width: '100%' }}>
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky left-0 z-20">
                製品CD
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky left-[7rem] z-20">
                製品名
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky left-[16rem] z-20">
                寸法
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky left-[24rem] z-20">
                単位
              </th>
              {dates.map((d) => (
                <th
                  key={d}
                  className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10 whitespace-nowrap"
                >
                  {formatDateLabel(d)}
                </th>
              ))}
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-100 sticky top-0 z-10 whitespace-nowrap">
                {todayLabel}(今日)
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {rows.map((row) => {
              const master = productMasterMap[row.productDisp] || {};
              const todayVal = todayValues[row.productDisp];
              return (
                <tr key={row.productDisp}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 bg-white sticky left-0 z-10">
                    {row.productDisp}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 bg-white sticky left-[7rem] z-10">
                    {master.product_name || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 bg-white sticky left-[16rem] z-10">
                    {master.product_size || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 bg-white sticky left-[24rem] z-10">
                    {master.tani || '-'}
                  </td>
                  {row.historyQuantities.map((qty, idx) => (
                    <td
                      key={`${row.productDisp}-${dates[idx]}`}
                      className="px-4 py-3 whitespace-nowrap text-sm text-right text-slate-700"
                    >
                      {qty}
                    </td>
                  ))}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-slate-900 bg-slate-50">
                    {readOnly ? (
                      <span>{todayVal ?? '-'}</span>
                    ) : (
                      <input
                        type="number"
                        className="w-24 px-2 py-1 text-right border border-slate-300 rounded text-sm"
                        value={todayVal ?? ''}
                        onChange={(e) => handleTodayChange(row.productDisp, e.target.value)}
                        onFocus={(e) => e.target.select()}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SupplierStockEditor;
